/**
 * @module services/catalogs/sruBase
 * @description Abstract base class for SRU (Search/Retrieve via URL) catalog services
 *
 * Provides common functionality for parsing SRU responses and XML handling.
 * Subclasses implement specific record parsing for different formats (UNIMARC, Dublin Core, etc.)
 */

import type { ItemData } from '@types';
import type { BaseCatalogRecord, BaseCatalogSearchResponse, SRUSearchOptions } from './types';
import { CATALOG_PROXY_PATHS } from './types';
import { validateCreatorTypes, validateFields, escapeCQL } from './utils';

/**
 * Abstract base class for SRU catalog services
 */
export abstract class SRUBaseService<
  TRecord extends BaseCatalogRecord = BaseCatalogRecord,
  TSearchOptions extends SRUSearchOptions = SRUSearchOptions,
> {
  /** Service name for logging */
  protected abstract readonly serviceName: string;

  /** Local proxy path (e.g., '/api/catalog/sudoc') — used instead of external CORS proxies */
  protected abstract readonly proxyPath: string;

  /** SRU version to use */
  protected readonly sruVersion: string = '1.2';

  /** Default record schema */
  protected abstract readonly defaultRecordSchema: string;

  /**
   * Execute an SRU search
   * Uses local reverse proxy (Vercel rewrites / Vite dev proxy) to avoid CORS issues.
   * @param cqlQuery CQL query string
   * @param options Search options
   */
  protected async executeSearch(
    cqlQuery: string,
    options: TSearchOptions = {} as TSearchOptions
  ): Promise<BaseCatalogSearchResponse<TRecord>> {
    const {
      startRecord = 1,
      maximumRecords = 10,
      recordSchema = this.defaultRecordSchema,
    } = options;

    // Build URL using local proxy path (same-origin, no CORS needed)
    const url = new URL(this.proxyPath, window.location.origin);
    url.searchParams.set('version', this.sruVersion);
    url.searchParams.set('operation', 'searchRetrieve');
    url.searchParams.set('query', cqlQuery);
    url.searchParams.set('recordSchema', recordSchema);
    url.searchParams.set('startRecord', String(startRecord));
    url.searchParams.set('maximumRecords', String(maximumRecords));

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`${this.serviceName} request failed: ${response.status}`);
    }

    const xmlText = await response.text();

    // Validate we got XML (not an error page)
    if (!xmlText.includes('<?xml') && !xmlText.includes('<searchRetrieveResponse')) {
      throw new Error(`${this.serviceName}: invalid response (not XML)`);
    }

    return this.parseSRUResponse(xmlText);
  }

  /**
   * Parse SRU XML response
   */
  protected parseSRUResponse(xmlText: string): BaseCatalogSearchResponse<TRecord> {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, 'text/xml');

    // Check for parse errors
    const parseError = doc.querySelector('parsererror');
    if (parseError) {
      throw new Error(`Failed to parse ${this.serviceName} response`);
    }

    // Get total results
    const totalResultsEl = this.findElementByLocalName(doc, 'numberOfRecords');
    const totalResults = totalResultsEl ? parseInt(totalResultsEl.textContent || '0', 10) : 0;

    // Parse records
    const records: TRecord[] = [];
    const recordDataElements = this.findAllElementsByLocalName(doc, 'recordData');

    for (const recordDataEl of recordDataElements) {
      try {
        const record = this.parseRecordData(recordDataEl);
        if (record) {
          records.push(record);
        }
      } catch {
        // Skip unparseable records
      }
    }

    return { totalResults, records };
  }

  /**
   * Parse a single record from recordData element
   * Must be implemented by subclasses
   */
  protected abstract parseRecordData(recordDataEl: Element): TRecord | null;

  /**
   * Validate creator types and fields for an item
   * Convenience method that calls shared utils
   */
  protected validateItem(item: Partial<ItemData>): void {
    validateCreatorTypes(item, this.serviceName);
    validateFields(item, this.serviceName);
  }

  /**
   * Escape CQL query text
   */
  protected escapeCQL(text: string): string {
    return escapeCQL(text);
  }

  // ==========================================================================
  // XML Utility Methods
  // ==========================================================================

  /**
   * Find element by local name (ignoring namespace)
   * Works with namespaced XML like SRU responses
   */
  protected findElementByLocalName(parent: Document | Element, localName: string): Element | null {
    // Try without namespace first
    let el = parent.querySelector(localName);
    if (el) return el;

    // Try with common SRU namespaces
    el = parent.querySelector(`srw\\:${localName}, zs\\:${localName}`);
    if (el) return el;

    // Fallback: search all elements and match by local name
    const allElements = parent.getElementsByTagName('*');
    for (const element of allElements) {
      if (element.localName === localName) {
        return element;
      }
    }
    return null;
  }

  /**
   * Find all elements by local name (ignoring namespace)
   */
  protected findAllElementsByLocalName(parent: Document | Element, localName: string): Element[] {
    const results: Element[] = [];
    const allElements = parent.getElementsByTagName('*');
    for (const element of allElements) {
      if (element.localName === localName) {
        results.push(element);
      }
    }
    return results;
  }

  /**
   * Get text content from elements with a specific local name
   */
  protected getElementValues(parent: Element, localName: string): string[] {
    const elements = this.findAllElementsByLocalName(parent, localName);
    return elements.map((el) => el.textContent?.trim()).filter((text): text is string => !!text);
  }

  /**
   * Get first text content from element with a specific local name
   */
  protected getElementValue(parent: Element, localName: string): string | null {
    const el = this.findElementByLocalName(parent, localName);
    return el?.textContent?.trim() || null;
  }
}

/**
 * Dublin Core specific base class
 * Used by BnF and Swisscovery services
 */
export abstract class DublinCoreBaseService<
  TRecord extends BaseCatalogRecord = BaseCatalogRecord,
  TSearchOptions extends SRUSearchOptions = SRUSearchOptions,
> extends SRUBaseService<TRecord, TSearchOptions> {
  protected readonly defaultRecordSchema = 'dublincore';

  /**
   * Get all DC values for a specific element name
   */
  protected getDCValues(dcEl: Element, localName: string): string[] {
    return this.getElementValues(dcEl, localName);
  }

  /**
   * Get first DC value for a specific element name
   */
  protected getDCValue(dcEl: Element, localName: string): string | null {
    return this.getElementValue(dcEl, localName);
  }
}
